import {Box, createTheme, CssBaseline, ThemeProvider, Typography,} from "@mui/material";
import {endOfMonth, format, subMonths} from "date-fns";
import {useEffect, useMemo, useRef, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {useReactToPrint} from "react-to-print";
import {get} from "../../../api/api";
import {getAdvancedMetricSummary, getSaasAllAdvancedMetricGraphData,} from "../../../api/expenseBreakdown";
import {getInsightData} from "../../../api/industryInsights";
import {getSaasVendorsAnalysis} from "../../../api/saas_api/vendorAnalysis";
import {useCurrentProfile} from "../../../ContextWrapper";
import {colorExpenseFormat} from "../../../utils";
import ClientDashboardLayout from "../ClientDashboardLayout";
import MarketingExpensesReport from "../MarketingExpensesReport";
import OperationsExpensesReport from "../OperationsExpensesReport";
import ProductsExpensesReport from "../ProductsExpensesReport";
import TeamExpensesReport from "../TeamExpensesReport";
import performanceDescriptors from "./performanceDescriptors";
import PerformanceTemplatePage from "./performanceTemplatePage";

const theme = createTheme({
  typography: {
    fontFamily: "'Rethink Sans', 'Inter', sans-serif",
  },
  palette: {
    background: {
      default: "#F4F6FF",
    },
    text: {
      primary: "#111827",
      secondary: "#6B7280",
    },
    primary: {
      main: "#536DEC",
      dark: "#4C63D7",
    },
  },
});

const descriptorToMetricType = {
  team: "team",
  marketing: "marketing",
  operations: "overhead",
  products: "cogs",
};

const toNumber = (val) => {
  const num = Number(val);
  return Number.isFinite(num) ? num : 0;
};

// Returns null for backend sentinel "-" or non-numeric values; used to
// distinguish "no target" from a genuine zero.
const toSafeNum = (val) => {
  if (val === "-" || val == null) return null;
  const num = Number(val);
  return Number.isFinite(num) ? num : null;
};

const formatCurrency = (value) =>
  Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(toNumber(value));

const formatPercent = (value, digits = 1) => {
  const num = toNumber(value);
  if (!Number.isFinite(num)) return "0%";
  return `${num > 0 ? "+" : ""}${num.toFixed(digits)}%`;
};

const formatAbsPercent = (value, digits = 1) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return null;
  return `${Math.abs(num).toFixed(digits)}%`;
};

const KPI_SUB_COLOR = {
  good: "#00B050",
  bad: "#FF5E5E",
  neutral: "rgba(23,25,35,0.7)",
};

const parseLocalYmdDate = (dateStr) => {
  if (!dateStr || typeof dateStr !== "string") return null;
  const [yearStr, monthStr, dayStr] = dateStr.split("-");
  const year = Number(yearStr);
  const month = Number(monthStr);
  const day = Number(dayStr);

  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) {
    return null;
  }
  if (month < 1 || month > 12) return null;
  if (day < 1 || day > 31) return null;

  const d = new Date(year, month - 1, day);
  return Number.isNaN(d.getTime()) ? null : d;
};

const toMonthDayLabel = (endDateStr, dayNum) => {
  const [year, month, day] = String(endDateStr || "")
    .split("-")
    .map(Number);
  if (!year || !month) return String(dayNum);
  const d = new Date(year, month - 1, Number(dayNum) || 1);
  return `${format(d, "MMM")}'${format(d, "dd")}`;
};

const buildAlertBody = (summary, label, industryPercent = null) => {
  if (!summary) return null;

  const changePct = Number(summary.change_from_last_month_pct);
  const changePctText = formatAbsPercent(changePct, 1);
  const changeDirection = Number.isFinite(changePct)
    ? changePct < 0
      ? "decreased"
      : changePct > 0
        ? "increased"
        : "stayed flat"
    : null;

  const variance = Number(summary.current_month_target_change);
  const varianceText = Number.isFinite(variance)
    ? formatCurrency(Math.abs(variance))
    : null;
  const varianceDirection = Number.isFinite(variance)
    ? variance === 0
      ? "on budget"
      : variance < 0
        ? "under budget"
        : "over budget"
    : null;

  const leadSentence = (() => {
    if (!changeDirection) return null;
    if (changeDirection === "stayed flat") {
      return `Your ${label} expenses stayed flat this month`;
    }
    if (!changePctText) return null;
    return `Your ${label} expenses have ${changeDirection} by ${changePctText} this month`;
  })();

  let summarySentence = leadSentence;
  if (summarySentence && varianceDirection) {
    if (varianceDirection === "on budget") {
      summarySentence = `${summarySentence} and you're on budget.`;
    } else if (varianceText) {
      summarySentence = `${summarySentence} and you're running ${varianceText} ${varianceDirection}.`;
    } else {
      summarySentence = `${summarySentence}.`;
    }
  } else if (summarySentence) {
    summarySentence = `${summarySentence}.`;
  }

  const pctOfIncome = Number(summary.main_percentage);
  const pctOfIncomeText = Number.isFinite(pctOfIncome)
    ? `${pctOfIncome.toFixed(1)}%`
    : null;
  let tipSentence = null;
  if (pctOfIncomeText && Number.isFinite(industryPercent)) {
    const industryText = `${industryPercent.toFixed(1)}%`;
    let comparison = "in line with";
    if (pctOfIncome < industryPercent) {
      comparison = "better than";
    } else if (pctOfIncome > industryPercent) {
      comparison = "above";
    }
    tipSentence = `Your ${label} costs are at ${pctOfIncomeText} of revenue — ${comparison} the industry average of ${industryText}.`;
  } else if (pctOfIncomeText) {
    tipSentence = `Your ${label} costs are at ${pctOfIncomeText} of revenue.`;
  }

  return [summarySentence, tipSentence].filter(Boolean).join(" ");
};

const buildOperationsAlert = (summary, fallback) => {
  if (!summary) return fallback;
  const body = buildAlertBody(summary, "operations", fallback?.industryPercent);
  if (!body) return fallback;
  return { ...(fallback || {}), body };
};

const buildCategoryAlert = (summary, fallback, label) => {
  if (!summary) return fallback;
  const body = buildAlertBody(summary, label, fallback?.industryPercent);
  if (!body) return fallback;
  return { ...(fallback || {}), body };
};

const PerformanceDescriptorPage = () => {
  const navigate = useNavigate();
  const { descriptor, customer_id: customerIdParam } = useParams();
  const { profile } = useCurrentProfile();

  const [storedCustomerId, setStoredCustomerId] = useState(() => {
    try {
      return sessionStorage.getItem("selectedCustomerId");
    } catch (error) {
      return null;
    }
  });

  const customer_id = useMemo(() => {
    const customers = Array.isArray(profile?.customers)
      ? profile.customers
      : [];

    const isStaff =
      profile?.type === "admin" || profile?.type === "accountant";

    // Staff users have an empty profile.customers array — never rely on it.
    // Resolve from URL param first, then sessionStorage, then first customer.
    if (isStaff) {
      return (
        customerIdParam ||
        storedCustomerId ||
        customers[0]?.id?.toString() ||
        null
      );
    }

    if (!customers.length) return null;

    const demoCustomers = customers.filter((c) => c?.is_demo);
    const nonDemo = customers.filter((c) => !c?.is_demo);

    const hasTrialLike = nonDemo.some((c) =>
      ["Trial Demo", "Unpublished"].includes(c?.frontend_account_type),
    );

    if (hasTrialLike && demoCustomers[0]?.id) {
      return demoCustomers[0].id;
    }

    if (storedCustomerId) return storedCustomerId;
    if (nonDemo[0]?.id) return nonDemo[0].id;
    if (customers[0]?.id) return customers[0].id;
    if (customerIdParam) return customerIdParam;

    return null;
  }, [customerIdParam, profile, storedCustomerId]);

  const descriptorKey = (descriptor || "").toLowerCase();
  const data = useMemo(() => {
    if (!descriptorKey) return null;
    return performanceDescriptors?.[descriptorKey] ?? null;
  }, [descriptorKey]);

  const metricType = descriptorToMetricType[descriptorKey];
  const [summary, setSummary] = useState(null);
  const [trend, setTrend] = useState(null);
  const [industryInsights, setIndustryInsights] = useState(null);
  const [loadingData, setLoadingData] = useState(false);
  const [vendorsData, setVendorsData] = useState([]);
  const [vendorsLoading, setVendorsLoading] = useState(false);
  const [categoryDateStr, setCategoryDateStr] = useState(null);

  const [range, setRange] = useState("6 Months");
  const [customer, setCustomer] = useState(null);
  const [isYearToggled, setIsYearToggled] = useState(() => {
    try {
      const stored = sessionStorage.getItem("performanceYtd");
      if (stored === null) return true; // default ON
      return stored === "true";
    } catch (error) {
      return true;
    }
  });

  const [selectedMonthEnd, setSelectedMonthEnd] = useState(() => {
    try {
      const stored = sessionStorage.getItem("performanceSelectedMonth");
      if (stored) {
        const d = parseLocalYmdDate(stored);
        if (d) return d;
      }
    } catch {
      /* ignore */
    }
    return endOfMonth(subMonths(new Date(), 1));
  });

  // For "Past 12 Months" mode, anchor to the last completed month (not today).
  const lastCompletedMonthEnd = useMemo(
    () => endOfMonth(subMonths(new Date(), 1)),
    [],
  );

  // "Month" mode uses the picker selection; "Past 12 Months" uses last completed month.
  const dashboardMonthEnd = useMemo(
    () => (isYearToggled ? lastCompletedMonthEnd : selectedMonthEnd),
    [isYearToggled, lastCompletedMonthEnd, selectedMonthEnd],
  );

  const dashboardDateStr = useMemo(
    () => format(dashboardMonthEnd, "yyyy-MM-dd"),
    [dashboardMonthEnd],
  );

  useEffect(() => {
    if (!customer_id || !profile) return;
    if (profile?.type !== "admin") {
      const found = profile?.customers?.find(
        (item) => String(item.id) === String(customer_id),
      );
      if (found) {
        setCustomer(found);
        return;
      }
    }

    const controller = new AbortController();
    get(`/api/v2/customers/${customer_id}/`, {}, false, controller.signal)
      .then((data) => {
        if (!controller.signal.aborted) setCustomer(data);
      })
      .catch(() => {});

    return () => controller.abort();
  }, [customer_id, profile]);

  const handleBack = () => {
    const isStaff =
      profile?.type === "admin" || profile?.type === "accountant";
    if (isStaff && customer_id) {
      navigate(`/client/${customer_id}/dashboard/performance-overview`);
    } else {
      navigate("/performance");
    }
  };

  const drilldownReportRef = useRef(null);

  const DrilldownReportComponents = {
    team: TeamExpensesReport,
    marketing: MarketingExpensesReport,
    operations: OperationsExpensesReport,
    products: ProductsExpensesReport,
  };
  const DrilldownReport = DrilldownReportComponents[descriptorKey] ?? null;

  const drilldownReportTitles = {
    team: "Team Expenses Report",
    marketing: "Marketing Expenses Report",
    operations: "Operations Expenses Report",
    products: "Products Expenses Report",
  };

  const handleDrilldownExport = useReactToPrint({
    contentRef: drilldownReportRef,
    documentTitle: `${drilldownReportTitles[descriptorKey] ?? "Performance Report"} ${dashboardDateStr}`,
    pageStyle: `
          @page { size: A4 portrait; margin: 0; }
          @media print {
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            .no-print { display: none !important; }
          }
        `,
  });

  useEffect(() => {
    const syncFromStorage = () => {
      try {
        setStoredCustomerId(sessionStorage.getItem("selectedCustomerId"));
      } catch (error) {
        setStoredCustomerId(null);
      }
    };

    window.addEventListener("selectedCustomerId-changed", syncFromStorage);
    return () =>
      window.removeEventListener("selectedCustomerId-changed", syncFromStorage);
  }, []);

  useEffect(() => {
    const syncYtd = () => {
      try {
        const stored = sessionStorage.getItem("performanceYtd");
        if (stored === null) {
          setIsYearToggled(true);
        } else {
          setIsYearToggled(stored === "true");
        }

        const monthStored = sessionStorage.getItem("performanceSelectedMonth");
        if (monthStored) {
          const d = parseLocalYmdDate(monthStored);
          if (d) setSelectedMonthEnd(d);
        }
      } catch (error) {
        setIsYearToggled(true);
      }
    };

    window.addEventListener("performance-ytd-changed", syncYtd);
    return () => window.removeEventListener("performance-ytd-changed", syncYtd);
  }, []);

  useEffect(() => {
    if (!customer_id || !metricType) return undefined;
    const controller = new AbortController();
    const endDateStr = dashboardDateStr;
    setLoadingData(true);
    setCategoryDateStr(endDateStr);

    (async () => {
      try {
        const trendRes = await getSaasAllAdvancedMetricGraphData(
          customer_id,
          endDateStr,
          controller.signal,
          {},
        ).catch(() => null);

        const trendData = (() => {
          const raw = trendRes?.data || {};
          if (!raw?.months || !raw?.metrics) return [];

          const METRIC_LABELS = {
            marketing: "Marketing Spend",
            team: "Team Spend",
            overheads: "Ops Spend",
            products: "Products",
            income: "Income",
            profit: "Profit",
          };

          return Object.entries(raw.metrics).map(([key, arr]) => ({
            label: METRIC_LABELS[key] || key,
            labels: raw.months,
            target: 0,
            values: Array.isArray(arr) ? arr.map((v) => toNumber(v)) : [],
          }));
        })();

        const summaryRes = await getAdvancedMetricSummary(
          customer_id,
          endDateStr,
          metricType,
          controller.signal,
          {
            adjusted: false,
            year: isYearToggled,
          },
        ).catch(() => null);

        if (!controller.signal.aborted) {
          setTrend(trendData);
          setSummary(summaryRes?.data || {});
        }
      } finally {
        if (!controller.signal.aborted) setLoadingData(false);
      }
    })();
    return () => controller.abort();
  }, [customer_id, metricType, dashboardDateStr, isYearToggled]);

  useEffect(() => {
    if (!customer_id) return undefined;
    const controller = new AbortController();
    const performanceGroup = customer?.performance_group || "average";
    const revenueGroup = customer?.revenue_group || "fiftyk";
    getInsightData(
      customer_id,
      performanceGroup,
      revenueGroup,
      controller.signal,
    )
      .then((res) => {
        if (!controller.signal.aborted) {
          setIndustryInsights(res?.data || null);
        }
      })
      .catch(() => {})
      .finally(() => {});

    return () => controller.abort();
  }, [customer_id, customer]);

  useEffect(() => {
    if (!customer_id || !metricType) return undefined;
    const controller = new AbortController();
    const endDateStr = dashboardDateStr;

    const buildTopVendors = (vendors = []) => {
      const rows = vendors
        .filter((vendor) => vendor)
        .map((vendor) => ({
          name: vendor.account || "Uncategorized",
          category: vendor.name || "Uncategorized",
          amount: toNumber(
            vendor.current_month_total,
          ),
        }))
        .sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount))
        .slice(0, 5)
        .map((row, idx) => ({ ...row, rank: idx + 1 }));

      return rows;
    };

    setVendorsLoading(true);
    getSaasVendorsAnalysis(
      customer_id,
      endDateStr,
      { tag: metricType, year: isYearToggled },
      controller.signal,
    )
      .then((res) => {
        if (!controller.signal.aborted) {
          setVendorsData(buildTopVendors(res?.data?.vendors || []));
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!controller.signal.aborted) {
          setVendorsLoading(false);
        }
      });

    return () => controller.abort();
  }, [customer_id, metricType, dashboardDateStr, isYearToggled]);

  const mergedData = useMemo(() => {
    if (!data) return null;
    const badgePct = isYearToggled
      ? summary?.change_from_last_ytd_avg_pct
      : summary?.change_from_last_month_pct;
    const dynamicBadge = badgePct
      ? {
          direction: badgePct >= 0 ? "up" : "down",
          text: `${formatPercent(badgePct, 1)} ${isYearToggled ? "vs Previous 12 Months" : "vs last month"}`,
        }
      : data.badge;

    const kpis = [...(data.kpis || [])];
    if (kpis.length >= 3 && summary) {
      const total = isYearToggled
        ? (summary.actual ?? summary.current_month_total ?? 0)
        : (summary.current_month_total ?? summary.actual ?? 0);

      // Use toSafeNum so the backend sentinel "-" (no target set) is treated
      // as null rather than being coerced to 0 / "0%" by the formatters.
      const pctOfIncome = toSafeNum(summary.main_percentage);

      // target_pct is provided when using SaasTargetAllocation fallback.
      // For legacy monthly/yearly targets, use pnl_value (percentage target) instead.
      const targetPct =
        toSafeNum(summary.target_pct) ?? toSafeNum(summary.pnl_value);

      const variance = toSafeNum(summary.current_month_target_change);
      const variancePct = toSafeNum(summary.current_target_change_pct);
      const varianceDirection =
        variance === null
          ? null
          : variance === 0
            ? "on target"
            : variance > 0
              ? "over target"
              : "under target";

      kpis[0] = {
        ...kpis[0],
        value: formatCurrency(total),
        subtext:
          pctOfIncome !== null
            ? `${formatAbsPercent(pctOfIncome)} of income`
            : kpis[0]?.subtext,
        subColor: colorExpenseFormat(targetPct, pctOfIncome),
      };

      // kpis[1] "Perfect P&L Variance": dollar difference between actual and target.
      // When a SaaS target exists but income is 0 we can't derive the dollar target,
      // so we show "No income data" rather than the confusing "No target set".
      const varianceSubtext = (() => {
        if (variance !== null && variancePct !== null && varianceDirection) {
          const pctText = formatAbsPercent(variancePct, 1);
          return pctText ? `${pctText} ${varianceDirection}` : null;
        }
        if (targetPct !== null && pctOfIncome === null) {
          return "No income data";
        }
        return "No target set";
      })();
      const varianceSubColor =
        varianceSubtext === "No income data" || varianceSubtext === "No target set"
          ? KPI_SUB_COLOR.neutral
          : varianceDirection === "under target"
            ? KPI_SUB_COLOR.good
            : varianceDirection === "over target"
              ? KPI_SUB_COLOR.bad
              : KPI_SUB_COLOR.neutral;
      kpis[1] = {
        ...kpis[1],
        value: variance !== null ? formatCurrency(variance) : "—",
        subtext: varianceSubtext,
        subColor: varianceSubColor,
      };

      // kpis[2] "Perfect P&L Income": actual income in the centre, with the
      // dollar difference between target and actual as subtext.
      const perfectIncomeTarget = toSafeNum(summary.perfect_pnl_income);
      const perfectIncomeActual = toSafeNum(summary.perfect_pnl_income_actual);
      const perfectIncomeDiff =
        perfectIncomeTarget !== null && perfectIncomeActual !== null
          ? perfectIncomeActual - perfectIncomeTarget
          : null;
      const perfectIncomeDirection =
        perfectIncomeDiff === null
          ? null
          : perfectIncomeDiff === 0
            ? "on target"
            : perfectIncomeDiff > 0
              ? "above target"
              : "under target";

      const kpi2Subtext = (() => {
        if (perfectIncomeDiff !== null && perfectIncomeDirection) {
          return `${formatCurrency(Math.abs(perfectIncomeDiff))} ${perfectIncomeDirection}`;
        }
        if (targetPct !== null && pctOfIncome === null) {
          return "No income data";
        }
        return "No target set";
      })();
      const kpi2SubColor =
        kpi2Subtext === "No income data" || kpi2Subtext === "No target set"
          ? KPI_SUB_COLOR.neutral
          : perfectIncomeDirection === "above target"
            ? KPI_SUB_COLOR.good
            : perfectIncomeDirection === "under target"
              ? KPI_SUB_COLOR.bad
              : KPI_SUB_COLOR.neutral;
      kpis[2] = {
        ...kpis[2],
        value:
          perfectIncomeTarget !== null
            ? formatCurrency(perfectIncomeTarget)
            : "—",
        subtext: kpi2Subtext,
        subColor: kpi2SubColor,
      };
    }

    const lineChart = { ...(data.lineChart || {}) };

    if (Array.isArray(trend)) {
      lineChart.values = trend.map((metric) => ({
        ...metric,
        values: Array.isArray(metric.values)
          ? metric.values.map((v) => Math.abs(toNumber(v)))
          : [],
      }));
    }

    const insightPercentMap = {
      operations: Number(industryInsights?.overhead_percentage),
      marketing: Number(industryInsights?.marketing_percentage),
      products: Number(industryInsights?.cogs_percentage),
      team: Number(industryInsights?.team_percentage),
    };
    const alertWithIndustry = data.alert
      ? {
          ...data.alert,
          industryPercent: insightPercentMap[descriptorKey],
        }
      : null;

    let alert =
      descriptorKey === "operations"
        ? buildOperationsAlert(summary, alertWithIndustry)
        : descriptorKey === "marketing"
          ? buildCategoryAlert(summary, alertWithIndustry, "marketing")
          : descriptorKey === "products"
            ? buildCategoryAlert(summary, alertWithIndustry, "product")
            : descriptorKey === "team"
              ? buildCategoryAlert(summary, alertWithIndustry, "team")
              : data.alert;
    if (alert) {
      alert = {
        ...alert,
        title: "Lucro AI Insights coming soon...",
        body: "",
      };
    }

    return {
      ...data,
      badge: dynamicBadge,
      kpis,
      lineChart,
      vendors: vendorsData.length ? vendorsData : data.vendors,
      alert,
      _loadingDynamic: loadingData,
    };
  }, [
    data,
    summary,
    trend,
    loadingData,
    vendorsData,
    descriptorKey,
    industryInsights,
    isYearToggled,
  ]);

  if (!data) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <ClientDashboardLayout
          title={null}
          contentMaxWidth="100%"
          contentAlign="left"
        >
          <Box sx={{ p: 3 }}>
            <Typography
              sx={{ fontSize: 18, fontWeight: 700, color: "#111827" }}
            >
              Unknown performance page
            </Typography>
            <Typography sx={{ mt: 1, color: "#6B7280" }}>
              We couldn&apos;t find the descriptor for this page.
            </Typography>
          </Box>
        </ClientDashboardLayout>
      </ThemeProvider>
    );
  }

  const safeSummary = Array.isArray(data.summary) ? data.summary : [];
  const safeChartData = Array.isArray(data.chartData) ? data.chartData : [];

  const templateLoading = loadingData || vendorsLoading || !summary || !trend;

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ClientDashboardLayout
        title={null}
        contentMaxWidth="100%"
        contentAlign="left"
      >
        <Box
          sx={{
            // `ClientDashboardLayout` already applies `p: 3` (24px) to the main content area.
            // Keep this wrapper unpadded so spacing-to-screen-edge stays consistent.
            px: 0,
            py: 0,
            width: "100%",
            maxWidth: "100%",
          }}
        >
          <PerformanceTemplatePage
            title={mergedData?.title || ""}
            subtitle={mergedData?.subtitle || ""}
            description={mergedData?.description || ""}
            badge={mergedData?.badge}
            kpis={mergedData?.kpis}
            alert={mergedData?.alert}
            lineChart={mergedData?.lineChart}
            donut={mergedData?.donut}
            table={mergedData?.table}
            vendors={mergedData?.vendors}
            customerId={customer_id}
            metricType={metricType}
            dashboardDateStr={dashboardDateStr}
            categoryDateStr={categoryDateStr}
            isYearToggled={isYearToggled}
            range={range}
            onRangeChange={setRange}
            summary={safeSummary}
            chartData={safeChartData}
            loading={templateLoading}
            onBack={handleBack}
            actions={{ onExport: handleDrilldownExport }}
          />
          {DrilldownReport && (
            <Box
              sx={{
                position: "absolute",
                top: -9999,
                left: -9999,
                width: "210mm",
                pointerEvents: "none",
                "& *": {
                  fontFamily:
                    "'Rethink Sans', Helvetica, sans-serif !important",
                },
              }}
            >
              <DrilldownReport
                ref={drilldownReportRef}
                customerId={customer_id}
                endDate={dashboardDateStr}
              />
            </Box>
          )}
        </Box>
      </ClientDashboardLayout>
    </ThemeProvider>
  );
};

export default PerformanceDescriptorPage;
